import { PII_LABEL_KEYS } from './anonymousPedigrees';

// PROBAND-ONLY LABEL CONTROL (TGCLOUD-1465).
//
// Juliana Lee needs to hand a physician a pedigree showing only the PROBAND's identity, with the
// relatives de-identified. Full anonymisation hides everyone including the proband, so her workaround
// was a text box or hand-editing the exported PDF.
//
// So this feature is exactly "anonymise everyone EXCEPT the proband", and it deliberately reuses
// `PII_LABEL_KEYS` rather than restating which labels are identifying. If that list ever changes, both
// features move together — they cannot drift apart on what counts as identifying, which for a document
// leaving the clinic is the failure that matters.
//
// Tim's decision: IDENTIFYING labels only. Age, diagnoses, test results and the G-number all stay on
// relatives, because a pedigree with no clinical content is not worth sharing — the point of the copy is
// that the physician can read it. The G-number specifically is how you refer to "the affected aunt"
// without naming her, and it is not in PII_LABEL_KEYS.

// Is the mode actually in force? It needs BOTH the switch and a designated proband.
//
// Tim's decision on the no-proband case: FAIL OPEN. "Remove Proband" is a real state (see
// pedigree/probandState.js — `cleared`), and a pedigree can also be legitimately proband-less. Hiding
// every relative's identity in that state would look identical to full anonymisation and could be
// mistaken for it on a shared document; that is worse than hiding nothing and saying so. The UI shows a
// warning instead, and this is the ONE place the rule lives so no call site can forget it.
export const probandOnlyActive = (labelsSet, probandPid) =>
  !!(labelsSet && labelsSet.probandOnlyLabels) &&
  probandPid !== null &&
  probandPid !== undefined;

// The label set that applies to ONE node.
//
// Returns the SAME OBJECT REFERENCE when nothing is suppressed — inactive mode, or the proband itself.
// That matters: this runs per node inside the label-layout accumulation and the render filters, so the
// no-op path must not allocate, and callers memoising on identity stay correct.
export const labelsForNode = (labelsSet, { isProband, active }) => {
  if (!active || isProband) return labelsSet;
  const scoped = { ...labelsSet };
  PII_LABEL_KEYS.forEach((key) => {
    scoped[key] = false;
  });
  return scoped;
};

// Convenience for the render paths, which know the node and the resolved proband pid. Kept separate from
// `labelsForNode` so the predicate can be evaluated ONCE per chart rather than once per node.
export const labelsForNodeAt = (labelsSet, node, probandPid) =>
  labelsForNode(labelsSet, {
    isProband:
      probandPid !== null &&
      probandPid !== undefined &&
      String(node?.pid ?? node?.data?.pid) === String(probandPid),
    active: probandOnlyActive(labelsSet, probandPid)
  });

// Whether the switch should be OFFERED at all, and what to say when it is inert.
//
// Hidden entirely on an anonymous deployment: there is no proband identity to preserve there, so
// offering the control would imply something the deployment does not permit.
export const probandOnlyControlState = ({ anonymous, probandPid }) => {
  if (anonymous) return { offered: false, inert: false, warning: null };
  if (probandPid === null || probandPid === undefined) {
    return {
      offered: true,
      inert: true,
      warning:
        'No proband is designated on this pedigree, so nothing is being hidden. Set a proband to use this.'
    };
  }
  return { offered: true, inert: false, warning: null };
};
